import { useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { Feature, ProductIds } from 'core'

import { Alert, Button, NavMenuItem, SideNavWrapper } from 'common/components'
import { useFeatureFlag, useLoginUserV2 } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

import ReportCard from './common/ReportCard'
import ReportCardV2 from './common/ReportCardV2'

const enum NavigationMapping {
  W2Amounts = 1,
  PCORIReports,
  CReports,
  Emails,
}

const Reports = () => {
  const history = useHistory()
  const location = useLocation()

  const { user } = useLoginUserV2()
  const canShowCReports = useFeatureFlag(Feature.C1095Report) && user.companyIsAle

  const reportsNavigationPathMapping = new Map<string, number>([
    ['/', NavigationMapping.W2Amounts as number],
    ['/pcori-reports', NavigationMapping.PCORIReports as number],
    ['/1095-c-reports', NavigationMapping.CReports as number],
  ])

  const reportsNavigationMenu: NavMenuItem[] = [
    {
      name: 'admin-dashboard.reports.left-navigation.w2-amounts-title',
      key: NavigationMapping.W2Amounts,
      path: '/',
      componentTitle: 'admin-dashboard.reports.w2-amounts.title',
      component: ReportCard,
      componentProps: { title: 'W-2 amounts' },
    },

    {
      name: 'admin-dashboard.reports.left-navigation.pcori-reports-title',
      key: NavigationMapping.PCORIReports,
      path: '/pcori-reports',
      component: ReportCard,
      componentTitle: 'admin-dashboard.reports.pcori-reports.title',
      componentProps: { title: 'PCORI Reports' },
    },
    ...(canShowCReports
      ? [
          {
            name: 'admin-dashboard.reports.left-navigation.1095-c-reports',
            key: NavigationMapping.CReports,
            path: '/1095-c-reports',
            componentTitle: 'admin-dashboard.reports.1095-c-reports.title',
            component: ReportCardV2,
            componentProps: {
              title: '1095-C Reports',
              description: (
                <div>
                  Per IRS requirements, Form 1095-C must be filed by all Applicable Large Employers (ALEs) and furnished
                  to any employee who is a full-time employee for one or more months of the calendar year. ALEs must
                  report that information for all twelve months of the calendar year for each employee.
                  <ul className="pl-22">
                    <li className="mt-4 font-12">
                      See:{' '}
                      <Button
                        type="link"
                        className="p-0 font-12 h-auto"
                        href="https://www.irs.gov/forms-pubs/about-form-1095-c"
                        target="_blank"
                      >
                        About Form 1095-C
                      </Button>
                    </li>
                  </ul>
                </div>
              ),
              alerts: (
                <Alert
                  showIcon
                  type="info"
                  message={'Pro tip: Have your employees update their Coverage and Dependents'}
                  description={
                    <div className="color-gray-8">
                      <div className="pb-8">
                        All PeopleKeep users have a Coverage and Dependents page in their personal profile. This page is
                        used to store information about their qualifying individual health insurance coverage and their
                        qualifying dependents for Attestation and 1095-C reporting purposes.
                      </div>
                      <div>
                        Encouraging employees to accurately input and update that information makes these reports - and
                        your job - that much easier!
                      </div>
                    </div>
                  }
                />
              ),
            },
          },
        ]
      : []),
  ]

  const getNavigationMenu = () => {
    switch (user.productId) {
      case ProductIds.QsehraId:
        return reportsNavigationMenu
      case ProductIds.IchraId:
        return reportsNavigationMenu.slice(1)
      default:
        return [reportsNavigationMenu[1]]
    }
  }

  useEffect(() => {
    if (user.productId !== ProductIds.QsehraId && location.pathname === `${ROUTE_PATHS.REPORTS}`) {
      history.push(`${ROUTE_PATHS.REPORTS}/pcori-reports`)
    }
  }, [location.pathname])

  const navigationPath = canShowCReports
    ? reportsNavigationPathMapping
    : new Map<string, number>([
        ['/', NavigationMapping.W2Amounts as number],
        ['/pcori-reports', NavigationMapping.PCORIReports as number],
      ])

  return (
    canShowCReports !== undefined && (
      <SideNavWrapper
        title="reports"
        rootPath={ROUTE_PATHS.REPORTS}
        navStartingPositon={NavigationMapping.W2Amounts}
        navigationPathMapping={navigationPath}
        navigationMenu={getNavigationMenu()}
      />
    )
  )
}

export default Reports
